import React, { useContext, useRef, useState, useEffect } from 'react';
// import { hot } from 'react-hot-loader/root';
import format from 'date-fns/format';
import useWindowSize from '../../hooks/useWindowSize';
import FilterButton from '../FilterButton';
import { ListingMapContext } from '../../context/ListingMapContext';
import useOutsideClick from '../../hooks/useOutsideClick';
import PaginationResults from './PaginationResults';
import DropdownContainer from '../DropdownContainer';
import DatesDropdown from './Dropdowns/DatesDropdown';
import GuestsDropdown from './Dropdowns/GuestsDropdown';
import PriceDropdown from './Dropdowns/PriceDropdown';
import SortDropdown from './Dropdowns/SortDropdown';

function RentalFilterBar({ type }) {
  const { width: windowWidth } = useWindowSize();

  const isMobile = !(windowWidth > 768);

  const {
    setDropdown,
    rentalSortBy,
    datesVisible,
    guestsVisible,
    priceVisible,
    sortByVisible,
    mapEndDate,
    mapStartDate,
    toggleFilters,
    guests,
    minPrice,
    maxPrice,
    bedrooms,
    bathrooms,
    types,
    rentalSearchAmenities,
    meta,
    clearAllFilters,
  } = useContext(ListingMapContext);

  const [datesButtonText, setDatesButtonText] = useState('Dates');
  const [filtersCount, setFiltersCount] = useState(0);

  const datesRef = useRef(null);
  const guestsRef = useRef(null);
  const priceRef = useRef(null);
  const sortRef = useRef(null);
  const filtersRef = useRef(null);

  // Change Filter Text
  useEffect(() => {
    const allFilters = [
      types,
      bedrooms,
      bathrooms,
      windowWidth <= 768 && minPrice,
      windowWidth <= 768 && maxPrice,
    ].filter(cur => {
      return cur;
    });

    if (rentalSearchAmenities.length)
      setFiltersCount(allFilters.length + rentalSearchAmenities.length);
    else setFiltersCount(allFilters.length);
  }, [
    rentalSearchAmenities.length,
    bathrooms,
    bedrooms,
    filtersCount,
    maxPrice,
    minPrice,
    types,
    windowWidth,
  ]);

  useEffect(() => {
    // Only change the filter button text when the end date is selected
    if (mapEndDate) {
      // Handle cases for different months selected
      if (format(mapStartDate, 'MMM') === format(mapEndDate, 'MMM')) {
        setDatesButtonText(
          `${format(mapStartDate, 'MMM')} ${format(mapStartDate, 'D')}-${format(
            mapEndDate,
            'D'
          )}`
        );
      } else {
        setDatesButtonText(
          `${format(mapStartDate, 'MMM')} ${format(mapStartDate, 'D')}-${format(
            mapEndDate,
            'MMM'
          )} ${format(mapEndDate, 'D')}`
        );
      }
    } else {
      setDatesButtonText('Dates');
    }
  }, [mapEndDate, mapStartDate]);

  const { per_page, current_page, total_count } = meta || {};

  return (
    <div className="filter-bar">
      <section className="filters-container">
        <div className="filter-button-group" ref={datesRef}>
          <FilterButton
            onClick={() => {
              toggleFilters(false);
              setDropdown('dates');
            }}
            className={`${
              mapEndDate && mapStartDate ? 'btn--dark' : 'btn--light'
            }`}
          >
            {datesButtonText}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={datesVisible}>
              <DatesDropdown visible={datesVisible} />
            </DropdownContainer>
          )}
        </div>
        <div className="filter-button-group" ref={guestsRef}>
          <FilterButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              toggleFilters(false);
              setDropdown('guests');
            }}
            className={`${guests > 0 ? 'btn--dark' : 'btn--light'}`}
          >
            {guests > 0 ? `${guests} Guests` : 'Guests'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={guestsVisible}>
              <GuestsDropdown />
            </DropdownContainer>
          )}
        </div>
        {/* Don't Show Price button on Mobile */}
        {windowWidth > 768 && (
          <div className="filter-button-group" ref={priceRef}>
            <FilterButton
              onClick={() => {
                toggleFilters(false);
                setDropdown('price');
              }}
              className={`${minPrice > 0 || maxPrice > 0 ? 'btn--dark' : ''}`}
            >
              {minPrice > 0 && !(maxPrice > 0) && `Min $${minPrice}`}
              {maxPrice > 0 && !(minPrice > 0) && `Max $${maxPrice}`}
              {minPrice > 0 && maxPrice > 0 && `$${minPrice} - $${maxPrice}`}
              {!(minPrice > 0) && !(maxPrice > 0) && `Price`}
            </FilterButton>
            {!isMobile && (
              <DropdownContainer visible={priceVisible}>
                <PriceDropdown />
              </DropdownContainer>
            )}
          </div>
        )}
        {bedrooms > 0 && (
          <div className="filter-button-group no-mobile">
            <FilterButton
              className="btn--dark"
              onClick={() => toggleFilters(true)}
            >
              {bedrooms}+ Bedrooms
            </FilterButton>
          </div>
        )}
        {bathrooms > 0 && (
          <div className="filter-button-group no-mobile">
            <FilterButton
              className="btn--dark"
              onClick={() => toggleFilters(true)}
            >
              {bathrooms}+ Bathrooms
            </FilterButton>
          </div>
        )}
        <div className="filter-button-group" ref={filtersRef}>
          <FilterButton
            className={`${filtersCount ? 'btn--dark' : 'btn--light'}`}
            onClick={() => {
              toggleFilters(true);
              setDropdown(null);
            }}
          >
            {filtersCount
              ? `${filtersCount} Filter${filtersCount > 1 ? 's' : ''}`
              : 'Filters'}
          </FilterButton>
        </div>
        <div className="filter-button-group" ref={sortRef}>
          <FilterButton
            className={`${rentalSortBy ? 'btn--dark' : 'btn--light'}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              toggleFilters(false);
              setDropdown('sort_by');
            }}
          >
            {rentalSortBy ? `By ${rentalSortBy.label}` : 'Sort By'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={sortByVisible}>
              <SortDropdown mapType={type} />
            </DropdownContainer>
          )}
        </div>
        {!isMobile && (
          <button
            type="button"
            name="Clear All Filters"
            title="Clear All Filters"
            aria-label="Clear All Filters"
            className="btn btn--small btn--link"
            onClick={() => {
              clearAllFilters(type);
            }}
          >
            Clear All
          </button>
        )}
        {total_count > 0 && !isMobile && (
          <PaginationResults
            firstIndex={per_page * current_page - per_page + 1}
            lastIndex={Math.min(per_page * current_page, total_count)}
            totalCount={total_count}
            queryType="properties"
          />
        )}
      </section>
    </div>
  );
}

export default RentalFilterBar;
